import { useContext, useEffect, useState } from "react";
import { updateNowPlaying } from "../AudioPlayer";
import { context } from "../Context";
import Title from "./Components/Title";
import { useAuth0 } from "react-auth0-spa";
import HomeImageGallery from "./Components/Home/HomeImageGallery";
import JGCard from "./Components/JGCard";
import {
  Box,
  Button,
  Grid,
  SvgIcon,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import moment from "moment";

import { ReactComponent as RadioTowerIcon } from "Assets/icons/radio-tower.svg";
import AlbumArt from "Pages/Components/Player/AlbumArt";
import { StationContext } from "StationsContext/Context";

function RecentlyPlayedSongItem({ song }: { song: RecentlyPlayedSong }) {
  return (
    <Box flex={1} sx={{ maxWidth: "160px", margin: "0 auto" }}>
      <Box textAlign="right" sx={{ mb: 0.5 }}>
        <Typography
          variant="caption"
          sx={(theme) => ({ color: theme.palette.text.secondary })}
        >
          {moment.duration(moment(song.when).diff(moment())).humanize()} ago
        </Typography>
      </Box>

      <AlbumArt src={song.img} size={160} alt={song.album} />

      <Box sx={{ mt: 1.5 }}>
        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
          {song.title}
        </Typography>
        <Typography
          variant="body2"
          sx={(theme) => ({ color: theme.palette.text.secondary })}
        >
          {song.artist}
        </Typography>
      </Box>
    </Box>
  );
}

let chipIndex = 0;

function RecentlyPlayedStation({
  songs,
  maxColumns,
}: {
  songs: RecentlyPlayedSong[];
  maxColumns: number;
}) {
  const { stations } = useContext(StationContext);
  const STATIONS = stations;
  const [id] = useState(chipIndex++);
  const theme = useTheme();
  const chipHeight = theme.jgComponents.RecentlyPlayed.chip.height;
  const station = STATIONS.find((x) => x.id === songs[0].station);
  const color = theme.palette[songs[0].station]?.main;

  return (
    <JGCard
      borderless
      sx={{
        background: theme.palette.background.light,
        height: "100%",
        pt: `${chipHeight / 2}px`,
        pb: 1.5,
      }}
      style={{ minWidth: "160px" }}
    >
      <JGCard
        id={`song-chip-${id}`}
        borderWidth={theme.shape.borderWidths[2]}
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          maxWidth: "100%",
          minWidth: maxColumns === 1 ? "100%" : undefined,
          transform: "translateY(-50%)",
        }}
        ContentProps={{
          sx: {
            display: "flex",
            alignItems: "center",
            height: theme.jgComponents.RecentlyPlayed.chip.height,
            p: theme.spacing(0, 2.5, 0, 2),
            color,
          },
        }}
      >
        <SvgIcon component={RadioTowerIcon} sx={{ mr: 1, fontSize: 20 }} />
        <Box>
          <Typography
            variant="body2"
            sx={{ fontWeight: "medium", lineHeight: 1.143 }}
          >
            {station?.name}
          </Typography>
          <Typography
            variant="caption"
            component="p"
            sx={{
              fontSize: "0.75rem",
              lineHeight: 1.167,
              opacity: 0.7,
            }}
          >
            {station?.description}
          </Typography>
        </Box>
      </JGCard>

      <Grid container columns={songs.length} spacing={1}>
        <context.Consumer>
          {(context) => {
            return songs.map((x, i) => {
              return (
                <Grid item xs={1} key={i}>
                  <RecentlyPlayedSongItem song={x} />
                </Grid>
              );
            });
          }}
        </context.Consumer>
      </Grid>
    </JGCard>
  );
}

const Home = (props) => {
  const { loginWithRedirect } = useAuth0();
  const theme = useTheme();
  const chipHeight = theme.jgComponents.RecentlyPlayed.chip.height;
  const [viewAll, setViewAll] = useState(false);
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isSm = useMediaQuery(theme.breakpoints.down("md"));
  const isMd = useMediaQuery(theme.breakpoints.down("lg"));
  const maxColumns = isXs ? 1 : isSm ? 2 : isMd ? 3 : 6;

  useEffect(() => {
    if (props.match.path === "/(car|cars)")
      window.open(
        "https://careasy.org/nonprofit/foundation-to-advance-jazz--jazz-groove",
        "_self"
      );
    try {
      updateNowPlaying();
    } catch (e) {}
  }, [props.location.pathname, props.match.path, loginWithRedirect]);

  function getRecentlyPlayedByStation(recentlyPlayed: RecentlyPlayedSong[]) {
    return recentlyPlayed.reduce((prev, cur, index) => {
      if (
        prev.length === 0 || // first element
        prev[prev.length - 1][0].station !== cur.station || // the current station is not the same as the last station
        (index + 1 > maxColumns && (index + 1) % maxColumns === 1) || // the current index indicates it needs a new row
        maxColumns === 1 // we only allow a single column
      ) {
        // Create a new group
        prev.push([cur]);
      } else {
        // Add to the last group
        prev[prev.length - 1].push(cur);
      }
      return prev;
    }, [] as RecentlyPlayedSong[][]);
  }

  function filterRecentlyPlayed(recentlyPlayed) {
    if (viewAll) return recentlyPlayed;
    return recentlyPlayed.filter((x, i) => i <= 5);
  }

  return (
    <Box>
      <HomeImageGallery />

      <Title value="" />
      <Grid container sx={{ p: isXs ? 5 : 10 }} spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="h4" component="h2" sx={{ flexGrow: 1 }}>
              Recently Played
            </Typography>
            <Button
              variant="outlined"
              color="neutral"
              size="small"
              onClick={() => setViewAll(!viewAll)}
            >
              View {viewAll ? "Less" : "All"}
            </Button>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2} columns={maxColumns}>
            <context.Consumer>
              {(context) => {
                return getRecentlyPlayedByStation(
                  filterRecentlyPlayed(context.recentlyPlayed)
                ).map((x, i) => {
                  return (
                    <Grid
                      item
                      xs={x.length}
                      sx={{ mt: `${chipHeight / 2 + 16}px` }}
                      key={i}
                    >
                      <RecentlyPlayedStation
                        songs={x}
                        maxColumns={maxColumns}
                      />
                    </Grid>
                  );
                });
              }}
            </context.Consumer>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Home;
